import { useEffect, useState } from 'react';
import { editName } from './MeshControls';
import { MeshProposalRail } from './MeshProposalRail';
import { holeSpots, said } from './drillParts';
import { sameBore } from './bores';
import type { ByPart } from './partModelSize';
import type { PartItem } from './partsRun';
import { makesOwnHole, shownHoleId, type StepHole } from './stepHoles';
import { invalidatePartModels } from './partModels';
import { cssRgb } from './sampleColour';
import { SURFACE_EDITS, dropEdits, editsOf, fetchProposals, redoEdit, undoneOf, type Job3D, type MeshEdit, type MeshProposal } from './threeDApi';
import { TableTitle } from './TableCard';
import { useLanguage } from '../../contexts/LanguageContext';

// WHERE A CHANGE CUT, PLUGGED OR DRILLED, in the part's millimetres on one of its faces (as a HoleMark is).
// WHAT IS SHOWN OF A PART: the state after its `at`-th change, or — `undone`, the k-th undone one — the
// part as it stands with where that change was. Its spots drawn in red either way.
export type PartView = { at: number | null; undone?: number; spots: EditSpot[] };
export type EditSpot = {
  face: string; u: number; v: number; diameter: number; depth: number | null; through: boolean;
};
type Spot = { face: string; u: number; v: number; id?: string };
const spotsIn = (e: MeshEdit): (Spot & { diameter: number; depth: number | null; through: boolean })[] => (
  e.op === 'drill_all' ? e.bores ?? []
    : e.face && e.u != null && e.v != null
      ? [{ face: e.face, u: e.u, v: e.v, diameter: e.diameter ?? 0, depth: e.depth ?? null, through: !!e.through }] : []);

// EVERY PART OF A JOINT, ITS HISTORY (Louis 2026-10-29): the same rail as the zoomed-in modal's (see
// MeshProposalRail), one per part, under the joint's viewer — what was done to the rail and the seat
// and in which order, and a change taken back off from where it shows. The proposals nobody applied
// hang off it as they do there.
export function PartHistories({ parts, items, models, joint, viewing, onView, onBusy, paint = false }: {
  // Each part with every hole it has, over the whole manual: what its drill entries are named by.
  parts: { id: string; label: string; job: Job3D; holes: StepHole[] }[];
  items: PartItem[];
  models: ByPart;
  // THE JOINT ON SCREEN'S HOLES, by id: the changes about them stand out, the others greyed.
  joint: Set<string>;
  // Which part is drawn at which point of its history, and how to say so — null `at`: as it stands.
  viewing: ({ job: number } & PartView) | null;
  onView: (job: number, view: PartView | null) => void;
  // A change being made to one of them: the viewer says so at once.
  onBusy: (busy: boolean) => void;
  // ITS PAINT ON THE RAIL TOO (materials, colours, grain): a part on its own (see SingleModels), where
  // a colour put on wrong is taken off from here. A joint's leaves it out (see `shown` below).
  paint?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div className="mt-4 flex flex-col gap-3">
      <TableTitle>{t('notice.joints.histories')}</TableTitle>
      {parts.map((p) => (
        <PartHistory
          key={p.job.id}
          part={p}
          items={items}
          models={models}
          joint={joint}
          viewing={viewing?.job === p.job.id ? viewing : null}
          onView={(view) => onView(p.job.id, view)}
          onBusy={onBusy}
          paint={paint}
        />
      ))}
    </div>
  );
}

const notPaint = (e: MeshEdit) => !SURFACE_EDITS.includes(e.op);

function PartHistory({ part, items, models, joint, viewing, onView, onBusy, paint }: {
  part: { id: string; label: string; job: Job3D; holes: StepHole[] };
  items: PartItem[];
  models: ByPart;
  joint: Set<string>;
  viewing: PartView | null;
  onView: (view: PartView | null) => void;
  onBusy: (busy: boolean) => void;
  paint: boolean;
}) {
  const { t } = useLanguage();
  const { label, job, holes } = part;
  // WHERE EACH OF ITS HOLES BORES IT, for an entry from before the bores carried their ids.
  const [spots, setSpots] = useState<Awaited<ReturnType<typeof holeSpots>>>([]);
  useEffect(() => {
    let live = true;
    void holeSpots(job, items[Number(part.id)], holes, items, models).then((got) => { if (live) setSpots(got); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, holes.map((h) => h.id).join(','), items, models, part.id]);
  // A DRILLING NAMED BY ITS HOLES (Louis 2026-10-29), one a line: its own ids, or the holes at its spots.
  // THE HOLES AT A SPOT: the one its entry names, or every one there — a mirror pair's two hands share
  // the file's spots. A plug is placed by its spot and said at the hole's width: the width is not asked.
  const holesAt = (b: Spot): StepHole[] => {
    const named = b.id ? holes.filter((h) => h.id === b.id) : [];
    return named.length ? named : spots.filter((p) => p.face === b.face && Math.abs(p.u - b.u) < 0.5
      && Math.abs(p.v - b.v) < 0.5).map((p) => p.hole);
  };
  // THE HOLES A CHANGE IS ABOUT: a drilling's, by their ids or their spots; one bore or plug's, by its spot.
  const holesOf = (e: MeshEdit): StepHole[] => spotsIn(e).flatMap(holesAt);
  const spotsOf = (e: MeshEdit): EditSpot[] => spotsIn(e).map((b) => ({
    face: b.face, u: b.u, v: b.v, diameter: b.diameter, depth: b.depth, through: b.through,
  }));
  // GREYED (Louis 2026-10-29): a change about holes, none of them this joint's. A change about the
  // whole part (a rescale, a repair) stands as it is.
  const faded = (e: MeshEdit) => {
    const about = holesOf(e);
    return about.length > 0 && !about.some((h) => joint.has(h.id));
  };
  const labelOf = (e: MeshEdit) => {
    if (e.op !== 'drill_all' || !e.bores?.length) return editName(e, t);
    const ids = e.bores.flatMap((b) => {
      const hole = b.id ? holes.find((h) => h.id === b.id) : spots.find((p) => sameBore(p, b))?.hole;
      if (!hole) return b.id ? [b.id] : [];
      return [shownHoleId(hole.id, hole.via != null && makesOwnHole(items[Number(hole.via)]))];
    });
    return ids.length === e.bores.length ? [...new Set(ids)].join('\n') : editName(e, t);
  };
  const [proposals, setProposals] = useState<MeshProposal[]>([]);
  const [busy, setBusyHere] = useState(false);
  const setBusy = (on: boolean) => { setBusyHere(on); onBusy(on); };
  const [problem, setProblem] = useState('');
  const edits = editsOf(job);
  const undone = undoneOf(job);
  useEffect(() => {
    let live = true;
    void fetchProposals(job.id).then((got) => { if (live) setProposals(got.proposals); }).catch(() => {});
    return () => { live = false; };
  }, [job.id, edits.length]);
  // A CHANGE TAKEN OFF, and the ones after it made again on what is left (see notice_3d._drop_edits).
  const unmake = async (indices: number[]) => {
    if (busy || !indices.length) return;
    setBusy(true);
    setProblem('');
    try {
      // KEPT ON THE HISTORY, crossed: undone by hand, so somebody may want it back.
      await dropEdits(job.id, indices, { keep: true });
      onView(null);
      invalidatePartModels();
    } catch (e) {
      setProblem(said(e));
    }
    setBusy(false);
  };
  // AN UNDONE DRILLING MADE AGAIN, from what it recorded (see notice_3d.redo_edit).
  const redo = async (k: number) => {
    if (busy) return;
    setBusy(true);
    setProblem('');
    onView(null);
    try {
      await redoEdit(job.id, k);
      invalidatePartModels();
    } catch (e) {
      setProblem(said(e));
    }
    setBusy(false);
  };
  return (
    <div className={`rounded-xl border border-slate-200 p-2 ${busy ? 'pointer-events-none opacity-60' : ''}`}>
      <div className="mb-1 text-center text-xs font-semibold text-slate-700">{label}</div>
      <MeshProposalRail
        edits={edits}
        labelOf={labelOf}
        faded={faded}
        viewingAt={viewing?.undone == null ? viewing?.at ?? null : null}
        // The end of the trunk is the part as it stands: back to it.
        // EVERY DOT SHOWS ITS ZONE, the last one's on the part as it stands; Hide puts the marks away.
        onView={(at) => onView({ at, spots: at > 0 ? spotsOf(edits[at - 1]) : [] })}
        onHide={() => onView(null)}
        undone={undone}
        // A DRILLING'S PLACE: its first hole's id, so the dots read H101, H102… (Louis 2026-10-29).
        orderOf={(e) => (e.op === 'drill_all' ? holesOf(e).map((h) => h.id).sort()[0] ?? null : null)}
        viewingUndone={viewing?.undone ?? null}
        onUndone={(k, what) => {
          if (what === 'see') onView(viewing?.undone === k ? null : { at: null, undone: k, spots: spotsOf(undone[k]) });
          else void redo(k);
        }}
        // ROOM FOR THE LONGEST LIST OF HOLES under its dot, a line each.
        caption={Math.max(62, ...edits.filter(notPaint).map((e) => labelOf(e).split('\n').length * 14 + 8))}
        tintOf={(e) => (e.op === 'colour' && e.color ? cssRgb(e.color) : undefined)}
        origin={{ label: t('notice.3d.origin'), when: job.createdAt }}
        proposals={proposals}
        openId={null}
        onOpen={() => {}}
        // A trunk dot is the state AFTER its edit: the entry it stands for is the one before it.
        onDelete={({ meshAt }) => { if (meshAt != null) void unmake([meshAt - 1]); }}
        onDeleteMany={(indices) => void unmake(indices)}
        // NOT ITS PAINT (Louis 2026-10-29): materials, colours and grain are Phase 3's, and their history
        // says nothing about the joint. Its shape's changes only.
        shown={paint ? undefined : notPaint}
      />
      {problem && <p className="mt-1 text-center text-xs text-rose-600">{problem}</p>}
    </div>
  );
}
